import { Link } from "react-router-dom";
import { site, products, images } from "@/data/store";
import { ProductCard } from "@/components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-black/50">{children}</p>
);

export const Hero = () => (
  <section data-testid="hero-section" className="mx-auto grid max-w-[1440px] gap-10 px-5 pb-20 pt-12 sm:px-8 md:grid-cols-12 md:items-center md:gap-12 md:pt-20 lg:px-12 lg:pb-28">
    <div className="fade-up md:col-span-6 lg:col-span-5">
      <Eyebrow>{site.tagline}</Eyebrow>
      <h1 data-testid="hero-headline" className="mt-6 font-serif text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
        {site.heroHeadline}
      </h1>
      <p className="mt-8 max-w-md text-base leading-relaxed text-black/65">{site.heroSubheadline}</p>
      <button
        type="button"
        data-testid="hero-shop-button"
        onClick={() => document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" })}
        className="mt-10 inline-flex h-12 items-center border border-black bg-black px-8 text-[13px] font-medium uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-transparent hover:text-black"
      >
        Shop Planners
      </button>
    </div>
    <div className="fade-up md:col-span-6 lg:col-span-7" style={{ animationDelay: "150ms" }}>
      <img src={images.hero} alt={`${site.brandName} planners`} className="aspect-[4/5] w-full object-cover md:aspect-[5/6] lg:aspect-[6/5]" />
    </div>
  </section>
);

export const Shop = () => (
  <section id="shop" data-testid="shop-section" className="border-t border-black/10">
    <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
      <div className="mb-12 flex flex-col gap-4 md:mb-16 md:flex-row md:items-end md:justify-between">
        <div>
          <Eyebrow>The Collection</Eyebrow>
          <h2 className="mt-4 font-serif text-4xl font-light sm:text-5xl">Three planners. One rhythm.</h2>
        </div>
        <p className="max-w-sm text-sm text-black/60">Every product is an instant PDF download, ready to use on any device or print.</p>
      </div>
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {products.map((p, i) => (
          <ProductCard key={p.slug} product={p} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const steps = [
  { title: "Choose your planner", text: "Pick the planner and package that fits your goals." },
  { title: "Secure checkout on Whop", text: "Pay safely through Whop's trusted checkout." },
  { title: "Instant download", text: "Download immediately — a copy is also sent to your email." },
];

export const HowItWorks = () => (
  <section id="how-it-works" data-testid="how-it-works-section" className="border-t border-black/10 bg-white">
    <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
      <Eyebrow>How it works</Eyebrow>
      <h2 className="mt-4 font-serif text-4xl font-light sm:text-5xl">Simple from start to page one.</h2>
      <ol className="mt-14 grid border-t border-black/10 md:grid-cols-3">
        {steps.map((s, i) => (
          <li key={s.title} data-testid={`how-step-${i}`} className={`border-b border-black/10 py-10 md:border-b-0 md:px-8 md:py-12 ${i > 0 ? "md:border-l" : "md:pl-0"}`}>
            <span className="font-serif text-6xl font-light text-black/20">0{i + 1}</span>
            <h3 className="mt-6 font-serif text-2xl">{s.title}</h3>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-black/60">{s.text}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const About = () => (
  <section id="about" data-testid="about-section" className="border-t border-black/10">
    <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 sm:px-8 md:grid-cols-2 md:items-center lg:gap-24 lg:px-12 lg:py-28">
      <img src={images.about} alt="Lifestyle" loading="lazy" className="aspect-[4/5] w-full object-cover" />
      <div>
        <Eyebrow>About {site.brandName}</Eyebrow>
        <h2 className="mt-4 font-serif text-4xl font-light leading-tight sm:text-5xl">{site.aboutTitle}</h2>
        <p className="mt-8 max-w-lg text-base leading-relaxed text-black/65">{site.aboutText}</p>
      </div>
    </div>
  </section>
);

export const Faq = () => (
  <section id="faq" data-testid="faq-section" className="border-t border-black/10">
    <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 sm:px-8 md:grid-cols-12 lg:px-12 lg:py-28">
      <div className="md:col-span-4">
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="mt-4 font-serif text-4xl font-light sm:text-5xl">Questions, answered.</h2>
      </div>
      <Accordion type="single" collapsible className="border-t border-black/15 md:col-span-8">
        {site.faq.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="border-black/15">
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-serif text-xl font-normal hover:no-underline sm:text-2xl">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="max-w-2xl pb-6 text-base leading-relaxed text-black/65">
              {f.a}
              {f.link && (
                <Link to={f.link.to} data-testid={`faq-link-${i}`} className="nav-link ml-1 text-black">
                  {f.link.label}
                </Link>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);
